import React from 'react';
import * as Yup from 'yup';
import { Box, Button, Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import AddIcon from '@mui/icons-material/Add';
import { SmallFormTextField } from '../../../../components/small-fields/SmallFormTextField';
import { DebounceInput } from 'react-debounce-input';
import { baseEventWidth, baseHouseHeight } from '../constants';

const alphabet = [
  'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm',
  'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z'
];

const ages = [
  { value: 'brak', label: 'Brak' }, { value: 3, label: '3' }, { value: 4, label: '4' },
  { value: 5, label: '5' }, { value: 6, label: '6' }, { value: 7, label: '7' },
  { value: 8, label: '8' }, { value: 9, label: '9' }, { value: 10, label: '10' },
  { value: 11, label: '11' }, { value: 12, label: '12' }, { value: 13, label: '13' },
  { value: 14, label: '14' }, { value: 15, label: '15' }, { value: 16, label: '16' },
  { value: 17, label: '17' }, { value: 18, label: '18' }, { value: 19, label: '19' }
];

export const generateUniqueId = (baseId, allNodes) => {
  for (const letter1 of alphabet) {
    for (const letter2 of alphabet) {
      const newId = `${baseId}${letter1}${letter2}`;

      // Sprawdzenie, czy istnieje domek z tym ID
      const houseExists = allNodes.some(eh => eh.id === newId);

      if (!houseExists) {
        return newId; // Zwróć nowy unikalny ID
      }
    }
  }

  return null;
};

export const AddNewHouseDialog = props => {
  const { ageRangeNodeData, open, setOpen, addNodesF, allNodes, setRecalculateHousePositions } = props;
  const validationSchema = Yup.object({
    houseName: Yup.string().required('To pole nie może być puste')
      .min(1),
    bedsCount: Yup.number().required('To pole nie może być puste')
      .moreThan(0, 'Domek musi mieć conajmniej 1 łóżko'),
    preferredGender: Yup.string().required('To pole nie może być puste')
  });

  const initialValues = {
    houseName: '',
    bedsCount: 0,
    preferredGender: 'brak',
    preferredAge: 'brak'

  };

  const prepareEmptyBeds = (bedsCount, houseId, ageRangeNodeDataId, ageRangeId, eventNode) => {
    const emptyBedsSet = [];
    const baseWidth = 180;
    const baseHeight = 30;
    let axisYShift = 44;
    for (let i = 0; i < bedsCount; i++) {
      emptyBedsSet.push({
        id: `${eventNode.id}${i}`,
        position: {
          x: (eventNode.style.width - baseWidth) / 2,
          y: (eventNode.style.height) - axisYShift
        },
        data: { houseId, ageRangeNodeDataId,
          ageRangeId,
          order: null,
          slot: i + 1 },
        style: { width: baseWidth, height: baseHeight },
        parentId: eventNode.id,
        extent: 'parent',
        className: 'light',
        type: 'BedNode',
        draggable: false
      });
      axisYShift += 39;
    }

    return emptyBedsSet;
  };

  const prepareEmptyEventNode = (ageRangeNodeData, newHouseNode) => {
    const eventHeight = newHouseNode.style.height - 28;
    const node = {
      id: `${newHouseNode.id}E`,
      position: {
        x: (newHouseNode.style.width - baseEventWidth) / 2,
        y: (newHouseNode.style.height - eventHeight) / 1.5
      },
      data: { houseId: newHouseNode.id,
        ageRangeNodeDataId: ageRangeNodeData.id,
        ageRangeId: ageRangeNodeData.data.id,
        eventId: '0000',
        eventName: 'Nie przypisano' },
      style: { width: baseEventWidth, height: eventHeight },
      parentId: newHouseNode.id,
      extent: 'parent',
      className: 'light',
      type: 'EventNode',
      draggable: false
    };

    return node;
  };

  const handleSubmit = values => {
    setOpen(false);
    const baseHeight = baseHouseHeight(values.bedsCount);
    const baseWidth = 220;
    const houseId = generateUniqueId(ageRangeNodeData.id, allNodes);
    const newHouseNode = {
      id: houseId,
      position: {
        x: ageRangeNodeData.measured.width / 2 - (baseWidth / 2),
        y: ageRangeNodeData.measured.height / 2 - (baseHeight / 2)
      },
      data: { label: values.houseName,
        preferredGender: values.preferredGender,
        preferredAge: values.preferredAge,
        bedsCount: values.bedsCount,
        ageRangeId: ageRangeNodeData.data.id },
      type: 'HouseNode',
      style: { width: baseWidth, height: baseHeight },
      parentId: ageRangeNodeData.id,
      extent: 'parent',
      className: 'light'
    };
    const emptyEventNode = prepareEmptyEventNode(ageRangeNodeData, newHouseNode);
    const newBedNodes = prepareEmptyBeds(values.bedsCount, houseId, ageRangeNodeData.id, ageRangeNodeData.data.id, emptyEventNode);

    addNodesF([newHouseNode, emptyEventNode, ...newBedNodes]);
    setRecalculateHousePositions([newHouseNode.id]);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  const genders = [{ value: 'brak', label: 'Brak' }, { value: 'boy', label: 'Chłopcy' }, { value: 'girl', label: 'Dziewczynki' }];

  return (
    <Dialog
      sx={{ zIndex: 9999 }}
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="add-new-house-dialog"
      fullWidth
    >
      <DialogTitle id="add-new-house-dialog">Dodaj Nowy Dom</DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
          <Box display="flex" flexDirection="column" mt={1} p={1} ml="5%" mr="5%">
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
              <Typography variant="subtitle2" flex="1 1 20%">Nazwa domu</Typography>
              <DebounceInput
                sx={{ flex: '1 1 80%' }}
                name="houseName"
                id="houseName"
                type="text"
                placeholder="nazwa domku"
                value={formik.values.houseName}
                onChange={formik.handleChange}
                error={formik.touched.houseName && Boolean(formik.errors.houseName)}
                helperText={formik.touched.houseName && formik.errors.houseName}
                debounceTimeout={100}
                element={SmallFormTextField}
                autoFocus
              />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
              <Typography variant="subtitle2" flex="1 1 20%">Ilość miejsc</Typography>
              <DebounceInput
                sx={{ flex: '1 1 80%' }}
                name="bedsCount"
                id="bedsCount"
                type="text"
                placeholder="ilość miejsc"
                value={formik.values.bedsCount}
                onChange={formik.handleChange}
                error={formik.touched.bedsCount && Boolean(formik.errors.bedsCount)}
                helperText={formik.touched.bedsCount && formik.errors.bedsCount}
                debounceTimeout={300}
                element={SmallFormTextField}
              />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
              <Typography variant="subtitle2" flex="1 1 20%">Preferowana płeć</Typography>
              <SmallFormTextField
                sx={{ flex: '1 1 80%' }}
                select
                id="preferredGender"
                name="preferredGender"
                options={genders}
                value={formik.values.preferredGender}
                onChange={formik.handleChange} />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
              <Typography variant="subtitle2" flex="1 1 20%">Preferowany wiek</Typography>
              <SmallFormTextField
                sx={{ flex: '1 1 80%' }}
                select
                id="preferredAge"
                name="preferredAge"
                options={ages}
                value={formik.values.preferredAge}
                onChange={formik.handleChange} />
            </Stack>
          </Box>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            sx={{ mt: 3 }}
            disabled={!formik.dirty}
          >
            <AddIcon />
          Zapisz
          </Button>
        </form>

      </DialogContent>
    </Dialog>
  );
};
